// Studio Claire: Property-Feld als Freitext, Dropdown, Farbfelder oder Pills,
// mit optionalem Zusatztext und variantenabhaengiger Sichtbarkeit.
//
// Bei Farbfeldern und Pills tragen die Bedienelemente NICHT den Property-Key:
// sie haben kein form-Attribut und sind damit nicht Teil des Warenkorb-
// Formulars. Den fertigen Wert schickt ein verstecktes Feld.
// Im Freitext-Modus schickt das Textfeld seinen Wert direkt.

class ProductPropertySelectComponent extends HTMLElement {
  connectedCallback() {
    this.textareaEl = this.querySelector('[data-role="textarea"]');
    this.counterEl = this.querySelector('[data-role="counter"]');
    this.selectEl = this.querySelector('[data-role="select"]');
    this.choices = Array.from(this.querySelectorAll('[data-role="choice"]'));
    this.hiddenEl = this.querySelector('[data-role="value"]');
    this.selectedLabel = this.querySelector('[data-role="selected-label"]');
    this.customWrap = this.querySelector('[data-role="custom-wrap"]');
    this.customInput = this.querySelector('[data-role="custom-input"]');

    if (this.textareaEl) {
      this.mode = 'textarea';
    } else if (this.choices.length > 0) {
      this.mode = 'choices';
    } else {
      this.mode = 'select';
    }

    this.multiple = this.getAttribute('data-multiple') === 'true';
    this.propertyName = this.getAttribute('data-property-name') || '';
    this.customToken = this.getAttribute('data-custom-token') || '';
    this.customLabel = this.getAttribute('data-custom-label') || '';
    this.separator = this.getAttribute('data-separator') || ', ';
    this.customSeparator = this.getAttribute('data-custom-separator') || ' — ';
    this.counterTemplate = this.getAttribute('data-counter-template') || '[used] / [max] Zeichen';
    this.validationMessage = this.getAttribute('data-validation-message') || '';
    this.formId = this.getAttribute('data-form-id') || '';
    this.isRequired = this.getAttribute('data-required') === 'true';
    this.visibleValues = (this.getAttribute('data-visible-values') || '')
      .split('|')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    const mapEl = this.querySelector('[data-role="variant-map"]');
    try {
      this.variantMap = mapEl ? JSON.parse(mapEl.textContent) : null;
    } catch (error) {
      this.variantMap = null;
    }

    this.handleChange = this.handleChange.bind(this);
    document.addEventListener('change', this.handleChange, true);
    document.addEventListener('input', this.handleChange, true);

    this.watchVariantInput();
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.handleChange, true);
    document.removeEventListener('input', this.handleChange, true);
    if (this.variantObserver) this.variantObserver.disconnect();
  }

  handleChange() {
    this.watchVariantInput();
    this.sync();
  }

  get variantInput() {
    let form = this.formId ? document.getElementById(this.formId) : null;
    if (!form) {
      const section = this.closest('.shopify-section');
      if (section) form = section.querySelector('form[action*="/cart/add"]');
    }
    if (!form) form = document.querySelector('form[action*="/cart/add"]');
    return form ? form.querySelector('input[name="id"]') : null;
  }

  watchVariantInput() {
    const input = this.variantInput;
    if (!input || input === this.watchedInput) return;
    if (this.variantObserver) this.variantObserver.disconnect();
    this.watchedInput = input;
    this.variantObserver = new MutationObserver(() => this.sync());
    this.variantObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
  }

  get selectedOptionValue() {
    const input = this.variantInput;
    if (!input || !this.variantMap) return null;
    const value = this.variantMap[String(input.value)];
    return value == null ? null : String(value).toLowerCase();
  }

  sync() {
    const hasRule = this.visibleValues.length > 0 && this.variantMap;
    const current = hasRule ? this.selectedOptionValue : null;
    const visible = !hasRule || (current !== null && this.visibleValues.indexOf(current) !== -1);

    this.hidden = !visible;

    if (!visible) {
      this.querySelectorAll('select, input, textarea').forEach((el) => {
        el.disabled = true;
        el.required = false;
      });
      return;
    }

    this.querySelectorAll('select, input, textarea').forEach((el) => {
      el.disabled = false;
    });

    if (this.mode === 'textarea') {
      this.syncTextarea();
    } else if (this.mode === 'choices') {
      this.syncChoices();
    } else {
      this.syncSelect();
    }
  }

  // Freitext-Modus: das Textfeld traegt den Property-Key selbst, die
  // Pflichtpruefung uebernimmt der Browser. Dazu ein Zeichenzaehler und eine
  // verstaendliche Fehlermeldung statt der Browser-Standardformulierung.
  syncTextarea() {
    this.textareaEl.required = this.isRequired;

    if (this.counterEl) {
      const max = this.textareaEl.getAttribute('maxlength') || '';
      this.counterEl.textContent = this.counterTemplate
        .replace('[used]', String(this.textareaEl.value.length))
        .replace('[max]', max);
    }

    if (this.validationMessage) {
      if (this.isRequired && this.textareaEl.value.trim() === '') {
        this.textareaEl.setCustomValidity(this.validationMessage);
      } else {
        this.textareaEl.setCustomValidity('');
      }
    }
  }

  syncSelect() {
    if (!this.selectEl) return;
    const useCustom = Boolean(this.customInput) && this.selectEl.value === this.customToken;

    if (this.customWrap) this.customWrap.hidden = !useCustom;

    if (useCustom) {
      this.selectEl.removeAttribute('name');
      this.selectEl.required = false;
      this.customInput.setAttribute('name', this.propertyName);
      this.customInput.required = this.isRequired;
    } else {
      this.selectEl.setAttribute('name', this.propertyName);
      this.selectEl.required = this.isRequired;
      if (this.customInput) {
        this.customInput.removeAttribute('name');
        this.customInput.disabled = true;
        this.customInput.required = false;
      }
    }
  }

  syncChoices() {
    let selected = this.choices.filter((input) => input.checked).map((input) => input.value);
    let useCustomPill = false;

    if (this.multiple) {
      selected = selected.filter((value) => value !== this.customToken);
    } else {
      useCustomPill = selected[0] === this.customToken;
      if (useCustomPill) selected = [];
    }

    const additive = this.multiple && Boolean(this.customInput);
    const showCustom = additive || useCustomPill;

    if (this.customWrap) this.customWrap.hidden = !showCustom;
    if (this.customInput) {
      this.customInput.disabled = !showCustom;
      this.customInput.required = useCustomPill && this.isRequired;
    }

    const text = showCustom && this.customInput ? this.customInput.value.trim() : '';
    let value = selected.join(this.separator);
    if (text) value = value ? value + this.customSeparator + text : text;

    if (this.hiddenEl) this.hiddenEl.value = value;
    if (this.selectedLabel) this.selectedLabel.textContent = value;
  }
}

if (!customElements.get('product-property-select-component')) {
  customElements.define('product-property-select-component', ProductPropertySelectComponent);
}
